import dayjs, { Dayjs } from "dayjs";
import {
  PurchaseOrderBranchRef,
  PurchaseOrderChain,
  PurchaseOrderCustomerRef,
  PurchaseOrderEvent,
  PurchaseOrderEventType,
  PurchaseOrderListItem,
  PurchaseOrderReturnInfo,
  PurchaseOrderStatus,
  PurchaseOrderStatusSummary,
} from "../../types/purchase-order.type";
import { DATE_PRESETS, DateRangePreset } from "../common/DateRangePicker";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";
import { formatDateTimeBkk } from "../../utils/date";

// ค่าคงที่และตัวช่วยล้วน ๆ ของจอ PO โมเดิร์นเทรด (W1, W5, W6, W7)
// ถ้อยคำไทยของสถานะ/เชน/ผลตรวจอยู่ใน src/types/purchase-order.type.ts ไม่พิมพ์ซ้ำที่นี่

/** จอ PO ของสาขา (W7) ต้องใช้งานได้ที่ 1024x600 จึงต้องมี override สำหรับจอเตี้ย */
export const PO_COMPACT_MEDIA = "@media " + COMPACT_QUERY;

/** รูปแบบวันที่ที่ API รับ (ทั้ง query ของรายการและของ Export) */
export const PO_API_DATE_FORMAT = "YYYY-MM-DD";

/** รูปแบบวันที่บนจอ ตามที่จอ back-office อื่นใช้ */
export const PO_DISPLAY_DATE_FORMAT = "DD/MM/YYYY";

/** ค่าที่ dropdown ใช้แทน "ไม่กรอง" — ตัวกรองจะแปลงเป็น undefined ก่อนส่ง API */
export const PO_UNFILTERED = "all";

export const PO_SEARCH_DEBOUNCE_MS = 500;

/** path ของ W1 รายการ PO (HO) */
export const PO_LIST_PATH = "/purchase-order";

/** query string ของ W1: เฉพาะ PO จากไฟล์นำเข้าครั้งนั้น (จอ W2 ส่งต่อมา) */
export const PO_LIST_BATCH_QUERY_KEY = "batch_uuid";

/**
 * query string ของ W1: สถานะตั้งต้น (เช่น ยังไม่ระบุสาขา ของไฟล์ที่เพิ่งบันทึก) · W1
 * อ่านครั้งเดียวตอนเปิดจอ หลังจากนั้นตัวกรองสถานะเป็นของผู้ใช้
 */
export const PO_LIST_STATUS_QUERY_KEY = "status";

/** ลิงก์ไป W1 ที่กรองเฉพาะไฟล์นำเข้าครั้งนั้น และ (ถ้าระบุ) เฉพาะสถานะนั้น */
export const buildPurchaseOrderListLink = (
  batchUuid: string,
  status?: PurchaseOrderStatus,
): string => {
  const query = new URLSearchParams();
  query.append(PO_LIST_BATCH_QUERY_KEY, batchUuid);
  if (status) query.append(PO_LIST_STATUS_QUERY_KEY, status);
  return `${PO_LIST_PATH}?${query.toString()}`;
};

/**
 * ขนาดหน้าของรายการ PO · สูงสุด 100 เท่ากับเพดาน uuid ต่อการเรียก ส่งให้สาขา หนึ่งครั้ง
 * พอดี เพราะ W1 ให้ติ๊กเลือกได้เฉพาะใบในหน้าที่แสดงอยู่
 */
export const PO_LIST_LIMIT_OPTIONS = [10, 20, 50, 100];
export const PO_DEFAULT_LIST_LIMIT = 20;

/**
 * เพดานของ POST /purchase-orders/publish ต่อหนึ่งครั้ง · การส่งทั้งไฟล์นำเข้าต้องใช้
 * batch_uuid (จอ W2 นำเข้าไฟล์ PO) ไม่ใช่การรวม uuid ข้ามหน้า
 */
export const PO_PUBLISH_MAX_UUIDS = 100;

export type PurchaseOrderStatusChipColor =
  | "secondary"
  | "error"
  | "warning"
  | "success"
  | "info"
  | "default";

/**
 * สีของสถานะ · ม่วง = ยังไม่ระบุสาขา ซึ่งเป็นงานที่ค้างอยู่ที่ HO · แดง = ยังไม่จับคู่ร้าน
 * ซึ่งเป็นงานที่ค้างอยู่ที่สาขา — แยกสีกันเพื่อให้รู้ทันทีว่างานค้างอยู่ฝั่งไหน
 */
export const PURCHASE_ORDER_STATUS_COLOR: Record<
  PurchaseOrderStatus,
  PurchaseOrderStatusChipColor
> = {
  NO_BRANCH: "secondary",
  RETURNED: "warning",
  UNMATCHED_CUSTOMER: "error",
  PENDING_PUBLISH: "warning",
  READY: "success",
  USED: "info",
  EXPIRED: "default",
};

/**
 * สถานะที่ใช้ chip แบบเส้นขอบ — สาขาส่งคืน ใช้สีส้มเดียวกับ รอส่งสาขา (งานรอ HO ทั้งคู่)
 * แต่ต้องแยกออกจากกันได้ทันทีบนตาราง จึงต่างกันที่รูปแบบแทนการหาสีที่เจ็ด
 */
export const PURCHASE_ORDER_STATUS_OUTLINED: ReadonlySet<PurchaseOrderStatus> =
  new Set<PurchaseOrderStatus>(["RETURNED"]);

/**
 * สีของสถานะในรูปเส้นทาง palette สำหรับการ์ดสรุป · ต่างจาก PURCHASE_ORDER_STATUS_COLOR
 * ที่เป็นชื่อ color ของ Chip เพราะ "default" ของ Chip ไม่มีอยู่ใน palette
 */
export const PURCHASE_ORDER_STATUS_ACCENT: Record<PurchaseOrderStatus, string> =
  {
    NO_BRANCH: "secondary.main",
    RETURNED: "warning.dark",
    UNMATCHED_CUSTOMER: "error.main",
    PENDING_PUBLISH: "warning.main",
    READY: "success.main",
    USED: "info.main",
    EXPIRED: "gray.main",
  };

/** ลำดับของแถบสรุปสถานะ ไล่ตามเส้นทางของ PO ตั้งแต่นำเข้าจนหมดช่วงใช้ */
export const PURCHASE_ORDER_STATUS_ORDER: PurchaseOrderStatus[] = [
  "NO_BRANCH",
  "RETURNED",
  "UNMATCHED_CUSTOMER",
  "PENDING_PUBLISH",
  "READY",
  "USED",
  "EXPIRED",
];

/** เชนที่กรองได้ ตามลำดับที่เสนอใน dropdown */
export const PURCHASE_ORDER_CHAIN_ORDER: PurchaseOrderChain[] = [
  "BIGC",
  "LOTUS",
];

/**
 * แปลงค่าที่ dropdown ถืออยู่ให้เป็นค่าที่ API รับ · ค่าที่ไม่ใช่เชน/สถานะที่รู้จัก
 * (รวมถึง PO_UNFILTERED) แปลว่าไม่กรองด้วยคอลัมน์นั้น
 */
export const toChainFilter = (
  value: string,
): PurchaseOrderChain | undefined =>
  PURCHASE_ORDER_CHAIN_ORDER.find((chain) => chain === value);

export const toStatusFilter = (
  value: string,
): PurchaseOrderStatus | undefined =>
  PURCHASE_ORDER_STATUS_ORDER.find((status) => status === value);

/** ค่าตั้งต้นก่อนคำตอบแรกจะมาถึง — ตัวเลขจริงทุกค่ามาจาก API เสมอ */
export const EMPTY_PURCHASE_ORDER_STATUS_SUMMARY: PurchaseOrderStatusSummary = {
  NO_BRANCH: 0,
  RETURNED: 0,
  UNMATCHED_CUSTOMER: 0,
  PENDING_PUBLISH: 0,
  READY: 0,
  USED: 0,
  EXPIRED: 0,
};

/**
 * สถานะที่ยังไม่ได้ส่งให้สาขาแน่ ๆ ตามลำดับการคำนวณสถานะของ backend: พร้อมใช้ กับ
 * ใช้แล้ว แปลว่าส่งไปแล้ว ส่วน หมดช่วงใช้ ส่งไปก็ใช้ไม่ได้อีก · ฝั่ง server ยังข้าม
 * ใบที่ส่งไปแล้วให้อีกชั้นหนึ่ง ตรงนี้เป็นแค่การไม่ให้ติ๊กใบที่ส่งไปแล้ว
 */
const PO_PUBLISHABLE_STATUSES: PurchaseOrderStatus[] = [
  "UNMATCHED_CUSTOMER",
  "PENDING_PUBLISH",
];

export const isPublishable = (row: PurchaseOrderListItem): boolean =>
  PO_PUBLISHABLE_STATUSES.includes(row.status);

/** ใบที่ HO ยังต้องระบุสาขาให้ (PATCH /purchase-orders/branch) */
export const needsBranch = (row: PurchaseOrderListItem): boolean =>
  row.status === "NO_BRANCH";

/**
 * เหตุผลที่ใบนี้ทำอะไรต่อไม่ได้แล้ว (ระบุ/ย้ายสาขา, ดึงกลับ, ส่งคืน) · null = ยังทำได้
 * ใช้เป็นข้อความ tooltip ของช่องติ๊กที่กดไม่ได้ และของเหตุผลที่ตัดออกจาก dialog
 */
export const lockedReason = (row: PurchaseOrderListItem): string | null => {
  if (row.status === "USED") return "ใช้แล้ว";
  if (row.status === "EXPIRED") return "เลยช่วงวันส่ง";
  return null;
};

/** ใบที่ HO ระบุหรือย้ายสาขาได้ — ทุกใบที่ยังไม่ใช้และยังไม่หมดช่วงใช้ */
export const isBranchAssignable = (row: PurchaseOrderListItem): boolean =>
  lockedReason(row) === null;

/** ใบที่ส่งให้สาขาแล้ว (อ่านจาก published_at ไม่เดาจากสถานะ) */
export const isPublished = (row: PurchaseOrderListItem): boolean =>
  row.published_at !== null && row.published_at !== undefined;

/**
 * ใบที่ HO ดึงกลับได้ — ส่งให้สาขาแล้วและยังไม่ใช้ · API รับใบหมดช่วงใช้ด้วย แต่จอปิดไว้
 * ให้ตรงกับช่องติ๊กที่ล็อกใบหมดช่วงใช้อยู่แล้ว (ดึงกลับไปก็ใช้ต่อไม่ได้)
 */
export const isRecallable = (row: PurchaseOrderListItem): boolean =>
  isPublished(row) && lockedReason(row) === null;

/**
 * ใบที่ส่งให้สาขาแล้วและจะถูกดึงกลับเมื่อย้ายไปสาขาที่เลือก · ย้ายไปสาขาเดิมไม่ดึงกลับ
 * (ยังส่งอยู่และสถานะส่งคืนไม่เปลี่ยน) · ยังไม่เลือกสาขา = นับทุกใบที่ส่งแล้ว
 */
export const countPublishedLeaving = (
  rows: PurchaseOrderListItem[],
  companyId: string,
): number =>
  rows.filter(
    (row) =>
      isPublished(row) && (!companyId || row.branch?.company_id !== companyId),
  ).length;

/** ใบที่สาขาส่งคืน HO ได้ — ส่งมาแล้ว ยังไม่ใช้ และยังไม่หมดช่วงใช้ (พร้อมใช้) */
export const isReturnable = (row: PurchaseOrderListItem): boolean =>
  row.status === "READY";

/**
 * ใบที่ติ๊กเลือกได้ใน W1 — ทุกใบที่ยังไม่ใช้และยังไม่หมดช่วงใช้ · แต่ละปุ่ม (ส่งให้สาขา,
 * ระบุ/ย้ายสาขา, ดึงกลับ) นับเฉพาะใบที่ตัวเองทำได้จากชุดที่เลือกไว้ชุดเดียว
 */
export const isSelectable = isBranchAssignable;

export interface BranchAssignPartition {
  assignable: PurchaseOrderListItem[];
  /** ใบที่ตัดออกพร้อมเหตุผล — ไม่ส่งไปให้ API ปฏิเสธทั้งคำขอด้วย 409 */
  excluded: { reason: string; count: number }[];
  /** มีใบที่ยังไม่ระบุสาขาหรือสาขาส่งคืน — ทางหลักของ dialog คือ บันทึกและส่งสาขา */
  prefersPublish: boolean;
}

/** แยกใบที่เลือกไว้ก่อนเปิด dialog ระบุ/ย้ายสาขา */
export const partitionBranchAssignable = (
  rows: PurchaseOrderListItem[],
): BranchAssignPartition => {
  const assignable: PurchaseOrderListItem[] = [];
  const excludedByReason = new Map<string, number>();

  rows.forEach((row) => {
    const reason = lockedReason(row);
    if (reason === null) {
      assignable.push(row);
      return;
    }
    excludedByReason.set(reason, (excludedByReason.get(reason) ?? 0) + 1);
  });

  return {
    assignable,
    excluded: Array.from(excludedByReason, ([reason, count]) => ({
      reason,
      count,
    })),
    prefersPublish: assignable.some(
      (row) => row.status === "NO_BRANCH" || row.status === "RETURNED",
    ),
  };
};

/** ชื่อเหตุการณ์ในประวัติของ PO (W6) ตามถ้อยคำที่ handbook กำหนดไว้ */
export const PURCHASE_ORDER_EVENT_LABEL: Record<PurchaseOrderEventType, string> =
  {
    IMPORTED: "นำเข้าจากไฟล์",
    PUBLISHED: "ส่งให้สาขา",
    CUSTOMER_MATCHED: "จับคู่ร้านโดยสาขา",
    USED: "ออกบิลแล้ว",
    RELEASED: "คืน PO (บิลถูกยกเลิก)",
    RETURNED: "สาขาส่งคืน HO",
    BRANCH_CHANGED: "ย้ายสาขา",
    UNPUBLISHED: "ดึงกลับจากสาขา",
  };

/** ข้อความแทนค่าว่างในตาราง ใช้ให้เหมือนกันทุกคอลัมน์ */
export const PO_EMPTY_TEXT = "-";

/**
 * รายละเอียดของเหตุการณ์หนึ่งบรรทัดใน W6 · payload มีเฉพาะฟิลด์ที่ชนิดของเหตุการณ์นั้น
 * เขียนไว้ จึงอ่านตามที่มีจริง ยกเว้น ย้ายสาขา ที่ต้องเล่าเป็น "เดิม → ใหม่"
 */
export const eventDetailText = (event: PurchaseOrderEvent): string => {
  const payload = event.payload;
  if (!payload) return "";

  let branchPart = "";
  if (event.type === "BRANCH_CHANGED") {
    branchPart = `สาขา ${payload.from_branch_code || "ยังไม่ระบุสาขา"} → ${
      payload.branch_code || payload.company_id || PO_EMPTY_TEXT
    }`;
  } else if (event.type === "RETURNED" && payload.branch_code) {
    branchPart = `จากสาขา ${payload.branch_code}`;
  }

  // รหัสในไฟล์ → ร้านที่เลือก → เลขบิล เล่าต่อกันเป็นเส้นทางของ PO ใบนี้
  const trail: string[] = [];
  if (payload.ship_to_code) trail.push(`รหัสในไฟล์ ${payload.ship_to_code}`);
  const shop = [payload.customer_code, payload.customer_name]
    .filter(Boolean)
    .join(" · ");
  if (shop) trail.push(shop);
  if (payload.doc_no) trail.push(`เลขที่บิล ${payload.doc_no}`);

  return [
    branchPart,
    trail.join(" → "),
    payload.reason ? `เหตุผล: ${payload.reason}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * วันที่ปฏิทินล้วน ("YYYY-MM-DD") ของ PO ไม่มีโซนเวลา จึงต้องอ่านตรง ๆ ห้ามแปลงผ่าน UTC
 * เหมือนค่า timestamp เพราะจะเลื่อนไปวันข้างเคียง
 */
export const formatCalendarDate = (date: string | null): string => {
  if (!date) return PO_EMPTY_TEXT;
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format(PO_DISPLAY_DATE_FORMAT) : PO_EMPTY_TEXT;
};

/** "รหัสร้าน · ชื่อร้าน" ของร้านที่จับคู่แล้ว · ว่างเมื่อ PO ยังไม่มีร้าน */
export const customerRefText = (
  customer: PurchaseOrderCustomerRef | null,
): string => {
  if (!customer) return "";
  return [customer.customer_code, customer.customer_name]
    .filter(Boolean)
    .join(" · ");
};

/**
 * "โดย ชื่อ · เวลา" ของการส่งคืน · เหตุผลแยกไว้แสดงเด่นกว่า
 */
export const returnByText = (info: PurchaseOrderReturnInfo): string =>
  [
    info.returned_by_name ? `โดย ${info.returned_by_name}` : "",
    formatDateTimeBkk(info.returned_at),
  ]
    .filter(Boolean)
    .join(" · ");

/** ข้อความแทนสาขาของใบที่ยังไม่ระบุสาขา (branch เป็น null) */
export const PO_NO_BRANCH_TEXT = "ยังไม่ระบุสาขา";

/**
 * "รหัสสาขา - ชื่อสาขา" · backend ส่ง branch_code/branch_name เป็น "" เมื่อไม่มีแถว
 * สาขาที่ยังใช้งานอยู่ถือ company_id นั้น จึงถอยไปแสดง company_id แทนการแสดงช่องว่าง
 * · null = PO ยังไม่ระบุสาขา
 */
export const branchRefText = (branch: PurchaseOrderBranchRef | null): string => {
  if (!branch) return PO_NO_BRANCH_TEXT;
  return (
    [branch.branch_code, branch.branch_name].filter(Boolean).join(" - ") ||
    branch.company_id
  );
};

/**
 * การติ๊กเลือกใบในหน้าที่แสดงอยู่ · W1 เลือกเพื่อส่ง/ระบุสาขา/ดึงกลับ ส่วน W7 เลือกเพื่อ
 * ส่งคืน HO — แต่ละจอบอกเองว่าใบไหนเลือกได้ และเหตุผลของใบที่เลือกไม่ได้
 */
export interface PurchaseOrderSelection {
  selected: ReadonlySet<string>;
  onChange: (selected: Set<string>) => void;
  canSelect: (row: PurchaseOrderListItem) => boolean;
  /** ข้อความ tooltip ของช่องติ๊กที่กดไม่ได้ · null = ไม่ต้องอธิบาย */
  disabledReason?: (row: PurchaseOrderListItem) => string | null;
}

/** W7: การจับคู่ร้านที่ค้าง ซึ่งเปิดเป็นแผงอยู่ใต้แถวที่ยังไม่มีร้าน */
export interface PurchaseOrderMatching {
  /** แถวที่ API คืนกลับมาหลังบันทึก ใช้แทนที่แถวเดิมโดยไม่ต้องอ่านรายการใหม่ */
  onMatched: (row: PurchaseOrderListItem) => void;
  /** เปิดฟอร์มลูกค้าเดิมเพื่อสร้างร้านใหม่ (แผงแสดง store number จากไฟล์ไว้ให้กรอกแล้ว) */
  onOpenNewCustomer: () => void;
  /** เพิ่มค่าเมื่อมีร้านใหม่ถูกสร้าง เพื่อให้ dropdown อ่านตัวเลือกใหม่ในครั้งถัดไป */
  refreshToken: number;
}

export interface PurchaseOrderRouteGroup {
  key: string;
  label: string;
  rows: PurchaseOrderListItem[];
}

/** หัวกลุ่มของแถวที่ร้านยังไม่มีสาย (รวมถึงใบที่ยังไม่จับคู่ร้าน) */
export const PO_ROUTE_UNASSIGNED_LABEL = "ไม่ระบุสาย";

/**
 * จัดกลุ่มแถวตามสายของร้าน สำหรับ W7 · เรียงตามลำดับที่ API ส่งมาโดยไม่สลับแถว และ
 * จัดกลุ่มเฉพาะแถวในหน้าที่แสดงอยู่ เพราะการแบ่งหน้าทำที่ server
 */
export const buildRouteGroups = (
  rows: PurchaseOrderListItem[],
): PurchaseOrderRouteGroup[] => {
  const groups: PurchaseOrderRouteGroup[] = [];
  const indexByKey = new Map<string, number>();

  rows.forEach((row) => {
    const key = row.route_number ?? "";
    const existingIndex = indexByKey.get(key);

    if (existingIndex === undefined) {
      indexByKey.set(key, groups.length);
      groups.push({
        key,
        label: row.route_number
          ? `สาย ${row.route_number}`
          : PO_ROUTE_UNASSIGNED_LABEL,
        rows: [row],
      });
      return;
    }

    groups[existingIndex].rows.push(row);
  });

  return groups;
};

const startOfToday = (): Dayjs => dayjs().startOf("day");

/**
 * ช่วงวันส่งเริ่มต้นของรายการ PO · ไฟล์ PO มาถึงก่อนวันส่ง ตัวกรองจึงต้องมองไปข้างหน้า
 * ไม่ใช่ย้อนหลังอย่างเดียวเหมือนจอรายงาน
 */
export const PO_DELIVERY_RANGE_BACK_DAYS = 7;
export const PO_DELIVERY_RANGE_FORWARD_DAYS = 14;

const NEXT_7_DAYS: DateRangePreset = {
  id: "po-next-7",
  label: "7 วันข้างหน้า",
  range: () => [startOfToday(), startOfToday().add(6, "day")],
};

const NEXT_14_DAYS: DateRangePreset = {
  id: "po-next-14",
  label: "14 วันข้างหน้า",
  range: () => [
    startOfToday(),
    startOfToday().add(PO_DELIVERY_RANGE_FORWARD_DAYS, "day"),
  ],
};

/**
 * ทางลัดของตัวเลือกช่วงวันส่ง · กลุ่มแรกมองไปข้างหน้า (ของที่กำลังจะส่ง) กลุ่มที่สอง
 * ย้อนหลังไว้ตามผลของที่ส่งไปแล้ว
 */
export const PO_DELIVERY_PRESET_GROUPS: DateRangePreset[][] = [
  [DATE_PRESETS.today, NEXT_7_DAYS, NEXT_14_DAYS],
  [DATE_PRESETS.yesterday, DATE_PRESETS.last7, DATE_PRESETS.thisMonth],
];
